/**
 * Group "craft" of the progress ops: assembling a weapon from a crafting stack and disassembling a weapon on the
 * 'crafting' screen.
 *
 * Original flow (new_interf): the stack is built by drag & drop (mouse_event MOUSE_DOWN creates the floating item,
 * floating_outside -> check_crafting_target drops it into weapon_stack[1..6] or weapon_diss, new_interf.ts:6566-6868);
 * the inventory lists only owned copies that are not already on the stack (render 2776-2862), so every stack entry is a
 * distinct owned copy. 'craft_assemble' (4730-4894) runs check_recipe (7505-7573); 'craft_disassemble' (4895-5003).
 * Both save with execute_after 'render' (or return early without a save, see craft.assemble).
 */
import { argsObject, boolArg, intArg, knownKey, optionalIntArg, stringArg } from '../args';
import type { RuleContext } from '../context';
import { RuleError } from '../errors';
import { defineOp } from '../registry';
import type { PlayerState } from '../state';
import { countCopies } from './progress-common';
import { weaponOptimisation } from './shop-common';

/** One crafting stack entry (weapon_stack[n] / weapon_diss of new_interf). */
export interface CraftItem {
  id: string;
  tp: 'weapon' | 'object';
  /** Upgrade rank of the weapon copy (1..10); objects have none. */
  range?: number;
  stolen?: boolean;
  crafted?: boolean;
}

export interface CraftAssembleArgs {
  /** The stack slots 1..6 in order; `null` is an empty slot. */
  stack: (CraftItem | null)[];
}

export interface CraftDisassembleArgs {
  item: CraftItem;
}

/** new_interf.crafting_result as the 'finish_crafting' screen reads it. */
export interface CraftResult {
  mode: 'assemble' | 'disassemble';
  /** Weapon id of the recipe result ('' = no recipe matched). */
  id: string;
  range: number;
  success: boolean;
  /** check_recipe's error: 'req<N>' when the Crafting perk is too low. */
  error: string;
  new_recipe: boolean;
  /** [count, junk number 1, ...]: obj_junk<N> added to the inventory. */
  junk: number[];
  /** Object found by a disassembly ('' = none). */
  new_object: string;
  /**
   * An early return of craft_assemble (new_interf.ts:4783-4790): the error screen the game showed instead of
   * 'finish_crafting'. The recipe was learned and the task advanced, nothing else changed.
   */
  errorScreen?: 'craft_too_diff' | 'craft_limit';
}

const MAX_RANGE = 10;

function parseItem(raw: unknown, field: string): CraftItem {
  const o = argsObject(raw, ['id', 'tp', 'range', 'stolen', 'crafted']);
  const id = stringArg(o, 'id', { maxLength: 64, pattern: /^[A-Za-z0-9_]+$/ });
  const tp = stringArg<'weapon' | 'object'>(o, 'tp', { oneOf: ['weapon', 'object'] });
  if (tp === 'weapon') {
    return {
      id,
      tp,
      range: intArg(o, 'range', { min: 1, max: MAX_RANGE }),
      stolen: o.stolen === undefined ? false : boolArg(o, 'stolen'),
      crafted: o.crafted === undefined ? false : boolArg(o, 'crafted'),
    };
  }
  // An object dragged onto the stack carries no copy fields (floating_weapon of an object: stolen/crafted false).
  optionalIntArg(o, 'range', { min: 1, max: MAX_RANGE });
  if (o.stolen !== undefined && boolArg(o, 'stolen')) throw new RuleError('bad_args', { field: `${field}.stolen`, problem: 'objects are never stolen' });
  if (o.crafted !== undefined && boolArg(o, 'crafted')) throw new RuleError('bad_args', { field: `${field}.crafted`, problem: 'objects are never crafted' });
  return { id, tp };
}

/** The crafting perk level check_recipe gets (new_interf.ts:4731-4737): perks.crafting, or 3 with specialization 4. */
function craftingLevel(p: PlayerState): number {
  let c = 0;
  if (p.perks['crafting']) c = p.perks['crafting'];
  if (p.spec == 4) c = 3;
  return c;
}

/** Stack slots as the handler sees them: [0, slot1, ..., slot6], an empty slot is {}. */
type Slots = any[];

function toSlots(stack: (CraftItem | null)[]): Slots {
  const slots: Slots = [0, {}, {}, {}, {}, {}, {}];
  stack.forEach((it, i) => {
    if (it) slots[i + 1] = it.tp === 'weapon' ? { id: it.id, stolen: it.stolen, crafted: it.crafted, range: it.range, tp: 'weapon' } : { id: it.id, stolen: false, crafted: false, range: undefined, tp: 'object' };
  });
  return slots;
}

/** new_interf.check_recipe (new_interf.ts:7505-7573), line by line. */
function checkRecipe(ctx: RuleContext, slots: Slots, recipes: number[], crafting: number): { id: string; range: number; error: string; prob: number } {
  const C = ctx.defs.CRAFT_DEF;
  crafting = Number(crafting);
  let id = '';
  let range = 1;
  let error = '';
  let prob = 5;
  let found = 0;
  for (let r = 1; r <= C[0]; r++) {
    const used: any[] = [0, false, false, false, false, false, false];
    for (let g = 1; g <= C[r].ingr[0]; g++) {
      for (let s = 1; s <= 6; s++) {
        if (!used[s] && slots[s].id == C[r].ingr[g]) {
          used[s] = true;
          ++used[0];
          break;
        }
      }
    }
    if (used[0] == C[r].ingr[0]) {
      found = r;
      break;
    }
  }
  if (found != 0) {
    id = C[found].result;
    if (crafting < C[found].req) error = 'req' + String(C[found].req);
    let min = 10;
    for (let s = 1; s <= 6; s++) {
      if (slots[s].tp == 'weapon') {
        if (Number(slots[s].range) < min) min = Number(slots[s].range);
      }
    }
    range = min > 5 ? 5 : min;
    if (!ctx.defs.WEAPONS_DEF[C[found].result].allow_upg) range = 1;
    if (recipes[found]) {
      if (recipes[found] == 1) prob = 100;
    } else {
      prob += crafting * 15;
    }
  }
  return { id, range, error, prob };
}

/** A weapon the crafting inventory lists (render 2783: count != 0 and not the 'skip_go' pseudo weapon). */
function listed(p: PlayerState, id: string): boolean {
  return Boolean(p.weapons[id]) && p.weapons[id][0] != 0 && id != 'skip_go';
}

/**
 * Ownership of a stack (the inventory offers each owned copy / object once): every weapon entry is an exact owned
 * copy (range, stolen, crafted), and no copy or object is used more often than the player has it.
 */
function checkOwnership(ctx: RuleContext, p: PlayerState, items: CraftItem[]): void {
  const need = new Map<string, number>();
  for (const it of items) {
    if (it.tp === 'weapon') {
      knownKey(ctx.defs.WEAPONS_DEF, it.id);
      if (!listed(p, it.id)) throw new RuleError('not_owned', { id: it.id });
    } else {
      knownKey(ctx.defs.OBJ_DEF, it.id);
    }
    const key = it.tp === 'weapon' ? `w\u0000${it.id}\u0000${it.range}\u0000${it.stolen}\u0000${it.crafted}` : `o\u0000${it.id}`;
    need.set(key, (need.get(key) ?? 0) + 1);
  }
  for (const it of items) {
    const key = it.tp === 'weapon' ? `w\u0000${it.id}\u0000${it.range}\u0000${it.stolen}\u0000${it.crafted}` : `o\u0000${it.id}`;
    const have = it.tp === 'weapon' ? countCopies(p, it.id, it.range!, it.stolen!, it.crafted!) : Number(p.objs[it.id]) || 0;
    if ((need.get(key) ?? 0) > have) throw new RuleError('not_owned', { id: it.id, tp: it.tp, have, need: need.get(key) });
  }
}

export const craftOps = {
  /**
   * craft.assemble { stack }: new_interf 'craft_assemble' (new_interf.ts:4730-4894) with check_recipe.
   * Preconditions: the button is visible only with at least 2 items and at least 1 weapon on the stack (render
   * 2989-3014); every entry is an owned copy/object (the handler never checked it, 4816-4817 even counted the gold of
   * a copy it did not find). Draws, in the original order: true_random(0, 99) against the recipe's chance (only when a
   * recipe matched), then on failure true_random(0, 99) against the junk chance 50 + 10 x Crafting, the junk count
   * and one true_random per junk part.
   * Kept rule: on success with a too-low Crafting perk ('req<N>') or at the weapon's limit the handler returns early
   * (craft_too_diff / craft_limit) AFTER advancing tasks 20/25/27/30 and learning the recipe, without consuming the
   * ingredients; the original did not save then, but the change stayed in memory and went into the next save, so the
   * rule stores it (result.errorScreen tells the client which error to show).
   */
  'craft.assemble': defineOp<CraftAssembleArgs, CraftResult>({
    parseArgs(raw) {
      const o = argsObject(raw, ['stack']);
      const s = o.stack;
      if (!Array.isArray(s)) throw new RuleError('bad_args', { field: 'stack', problem: 'must be an array' });
      if (s.length > 6) throw new RuleError('bad_args', { field: 'stack', problem: 'at most 6 slots' });
      return { stack: s.map((x, i) => (x === null ? null : parseItem(x, `stack.${i}`))) };
    },
    run(state, { stack }, ctx) {
      const p = state.player;
      const W = ctx.defs.WEAPONS_DEF;
      const O = ctx.defs.OBJ_DEF;
      const C = ctx.defs.CRAFT_DEF;
      const items = stack.filter((x): x is CraftItem => x !== null);
      const weaponsOnStack = items.filter((x) => x.tp === 'weapon').length;
      if (!(items.length > 1 && weaponsOnStack > 0)) throw new RuleError('not_available', { items: items.length, weapons: weaponsOnStack });
      checkOwnership(ctx, p, items);

      const slots = toSlots(stack);
      const crafting = craftingLevel(p);
      const recipe = checkRecipe(ctx, slots, p.recipes, crafting);
      const res: CraftResult = {
        mode: 'assemble',
        id: recipe.id,
        range: Number(recipe.range),
        error: recipe.error,
        new_recipe: false,
        junk: [0, 0],
        new_object: '',
        success: false,
      };
      let atLimit = false;
      if (res.id != '') {
        if (p.weapons[res.id]) {
          if (p.weapons[res.id][0] >= W[res.id].limit) atLimit = true;
        }
      }
      if (res.id != '' && recipe.prob > ctx.rng.int(0, 99)) res.success = true;
      if (res.success) {
        if (p.cur_task == 20) p.cur_task = 21;
        if (W[res.id].wpclass >= 3 && p.cur_task == 25) p.cur_task = 26;
        if (W[res.id].wpclass >= 4 && p.cur_task == 27) p.cur_task = 28;
        if (W[res.id].wpclass >= 5 && p.cur_task == 30) p.cur_task = 31;
        for (let r = 1; r <= C[0]; r++) {
          if (C[r].result == res.id) {
            if (p.recipes[r] == 0) {
              p.recipes[r] = 1;
              res.new_recipe = true;
              break;
            }
          }
        }
        if (res.error != '') return { state, result: { ...res, errorScreen: 'craft_too_diff' } };
        if (atLimit) return { state, result: { ...res, errorScreen: 'craft_limit' } };
        const copy = { range: res.range, stolen: false, crafted: true };
        if (!p.weapons[res.id]) {
          p.weapons[res.id] = [1, copy];
        } else {
          ++p.weapons[res.id][0];
          p.weapons[res.id][p.weapons[res.id][0]] = copy;
        }
        if (p.achiev['crafted']) ++p.achiev['crafted'];
        else p.achiev['crafted'] = 1;
      } else {
        res.error = '';
      }

      // Ingredients are consumed on success and failure alike.
      let gold = 0;
      for (let s = 1; s <= 6; s++) {
        const it = slots[s];
        if (!it.tp) continue;
        if (it.tp == 'weapon') {
          const range = Number(it.range);
          if (p.weapons[it.id]) {
            gold += W[it.id].gold;
            for (let c = 1; c <= p.weapons[it.id][0]; c++) {
              const w = p.weapons[it.id][c];
              if (w.range == range && w.stolen == it.stolen && w.crafted == it.crafted) {
                --p.weapons[it.id][0];
                p.weapons[it.id][c] = {};
                weaponOptimisation(p.weapons);
                break;
              }
            }
          }
        } else {
          gold += O[it.id].gold;
          --p.objs[it.id];
          if (p.objs[it.id] < 0) p.objs[it.id] = 0;
        }
      }
      if (!res.success) {
        const junkChance = 50 + crafting * 10;
        if (junkChance > ctx.rng.int(0, 99)) {
          if (gold < 4000) {
            res.junk = [0, 0];
          } else if (gold < 10000) {
            res.junk = [ctx.rng.int(1, 2), 0];
            for (let j = 1; j <= res.junk[0]; j++) res.junk[j] = ctx.rng.int(1, 4);
          } else if (gold < 20000) {
            res.junk = [ctx.rng.int(2, 4), 0];
            for (let j = 1; j <= res.junk[0]; j++) res.junk[j] = ctx.rng.int(1, 8);
          } else if (gold < 50000) {
            res.junk = [ctx.rng.int(4, 7), 0];
            for (let j = 1; j <= res.junk[0]; j++) res.junk[j] = ctx.rng.int(5, 8);
          } else {
            res.junk = [ctx.rng.int(7, 10), 0];
            for (let j = 1; j <= res.junk[0]; j++) res.junk[j] = ctx.rng.int(5, 10);
          }
        } else {
          res.junk = [0, 0];
        }
      }
      if (res.junk[0] != 0) {
        for (let j = 1; j <= res.junk[0]; j++) {
          const name = 'obj_junk' + String(res.junk[j]);
          if (!p.objs[name]) p.objs[name] = 1;
          else if (p.objs[name] < O[name].limit) ++p.objs[name];
        }
      }
      weaponOptimisation(p.weapons);
      return { state, result: res };
    },
  }),

  /**
   * craft.disassemble { item }: new_interf 'craft_disassemble' (new_interf.ts:4895-5003).
   * Preconditions: check_crafting_target puts into the disassembly slot only a weapon that is the result of a recipe
   * (6823-6854; "base" weapons are refused) and the copy comes from the inventory (owned exactly).
   * Unknown recipe: the recipe is learned and the weapon is lost (no draw). Known recipe: true_random(0, 99) against
   * 10 + 20 x Crafting; on success true_random(1, 8) for a junk part, then the recipe's object ingredients (x1/x2/x3
   * by weapon class) join the pool and true_random(1, pool size) picks the object. Task 15 -> 16.
   */
  'craft.disassemble': defineOp<CraftDisassembleArgs, CraftResult>({
    parseArgs(raw) {
      const o = argsObject(raw, ['item']);
      if (o.item === undefined) throw new RuleError('bad_args', { field: 'item', problem: 'required' });
      const item = parseItem(o.item, 'item');
      if (item.tp !== 'weapon') throw new RuleError('bad_args', { field: 'item.tp', problem: 'only weapons can be disassembled' });
      return { item };
    },
    run(state, { item }, ctx) {
      const p = state.player;
      const W = ctx.defs.WEAPONS_DEF;
      const O = ctx.defs.OBJ_DEF;
      const C = ctx.defs.CRAFT_DEF;
      knownKey(W, item.id);
      let isResult = false;
      for (let r = 1; r <= C[0]; r++) {
        if (C[r].result == item.id) {
          isResult = true;
          break;
        }
      }
      if (!isResult) throw new RuleError('not_available', { id: item.id, problem: 'base weapon' });
      if (!listed(p, item.id) || countCopies(p, item.id, item.range!, item.stolen!, item.crafted!) < 1) throw new RuleError('not_owned', { id: item.id });

      const crafting = craftingLevel(p);
      let known = false;
      for (let r = 1; r <= C[0]; r++) {
        if (C[r].result == item.id) {
          if (p.recipes[r] == 1) known = true;
          break;
        }
      }
      const res: CraftResult = {
        mode: 'disassemble',
        id: item.id,
        range: Number(item.range),
        error: '',
        new_recipe: !known,
        success: true,
        junk: [0, 0],
        new_object: '',
      };
      for (let r = 1; r <= C[0]; r++) {
        if (C[r].result == res.id) {
          if (p.recipes[r] == 0) {
            p.recipes[r] = 1;
            break;
          }
        }
      }
      if (known) {
        const chance = 10 + crafting * 20;
        if (chance > ctx.rng.int(0, 99)) {
          const pool: any[] = [1, 'obj_junk' + String(ctx.rng.int(1, 8))];
          for (let r = 1; r <= C[0]; r++) {
            if (C[r].result == res.id) {
              for (let g = 1; g <= C[r].ingr[0]; g++) {
                if (String(C[r].ingr[g]).search('obj_') != -1) {
                  let times = 1;
                  const wpclass = W[C[r].result].wpclass;
                  if (wpclass == 5) times = 2;
                  if (wpclass == 6) times = 2;
                  if (wpclass == 7) times = 3;
                  if (wpclass == 8) times = 3;
                  for (let t = 1; t <= times; t++) {
                    ++pool[0];
                    pool[pool[0]] = C[r].ingr[g];
                  }
                }
              }
            }
          }
          res.new_object = pool[ctx.rng.int(1, pool[0])];
        }
      }
      if (res.new_object != '') {
        if (!p.objs[res.new_object]) p.objs[res.new_object] = 1;
        else if (p.objs[res.new_object] < O[res.new_object].limit) ++p.objs[res.new_object];
      }
      const range = Number(item.range);
      if (p.weapons[item.id]) {
        for (let c = 1; c <= p.weapons[item.id][0]; c++) {
          const w = p.weapons[item.id][c];
          if (w.range == range && w.stolen == item.stolen && w.crafted == item.crafted) {
            --p.weapons[item.id][0];
            p.weapons[item.id][c] = {};
            break;
          }
        }
      }
      if (p.cur_task == 15) p.cur_task = 16;
      weaponOptimisation(p.weapons);
      return { state, result: res };
    },
  }),
};
